import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import {
	getPrimaryColor,
	getUnreadNotesIndexAndCount,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import { workOrdersRestCrudThunksForSupplier } from '../../thunks/work_orders_thunks';
import {
	fetchInternalNotesSubscribersForSupplier,
	updateInternalNotesSubscribersForSupplier,
} from '../../thunks/work_orders_subscribers_thunks';
import {
	NOTE_TYPE_LOG,
	NOTE_TYPE_INTERNAL_ATTACHMENT_LOG,
	NOTE_TYPE_SERVICE_CALL_CHECK_IN,
	NOTE_TYPE_SERVICE_CALL_CHECK_OUT,
} from '../../constants/NoteTypes';
import { EmptyState } from '../empty_state/EmptyState';
import { fetchInternalWorkspaceForSupplier } from '../../thunks/messages_thunks';
import MessageLog from '../MessageLog';
import ServiceCallLogEntry from '../ServiceCallLogEntry';
import { getWorkOrderSelector } from '../../selectors/workOrderSelectors';
import {
	getInternalNotesChannelId,
	getSanitisedWorkOrder,
	isNoteValid,
	getOwnThreadWOIdsToListenForWorkOrder,
} from '../../utils/WorkOrderUtils';
import { formatAndSortUserContacts } from '../../utils/DataFormatterUtils';
import { WebSocketContext } from '../../context/WebSocketContext';
import WebSocketTypingManager from '../work_orders_detail_notes_page/WebSocketTypingManager';
import { WEB_SOCKET_ENTITY_TYPES } from '../../utils/WebSocketUtils';
import SeparatorWithText from '../common/SeperatorWithText';
import { NOTE_INPUT_ID } from '../../utils/DomUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import WorkOrderNotesSubscribersPanel from '../work_orders_detail_notes_page/WorkOrderNotesSubscribersPanel';
import InternalWorkspaceWorkOrderNoteComposer from './InternalWorkspaceWorkOrderNoteComposer';
import MessageBubble from '../MessageBubble';
import useMountEffect from '../../hooks/useMountEffect';

type WsAdapterState = {
	typingDetails: any[];
	newNoteCount: number;
	refreshCount: number;
};

const scrollChatToBottom = () => {
	const element = document.getElementById('internalWorkspaceChatBox');
	if (element) {
		element.scrollTop = element.scrollHeight;
	}
};

interface InternalWorkspaceNotesTabProps {
	workOrder: any;
	messages: any;
	currentUser: any;
	searched_subscribers: any;
	match: { params: { id: string } };
	history: any;
	isClientView: boolean;
	getWorkOrder: (id: string) => Promise<any>;
	fetchMessages: (
		workOrderId: string,
		fetchOpts?: { clearChannelBeforeFetch?: boolean }
	) => Promise<any>;
	fetchUsers: (search: any, woId: any, filters: any, workOrder: any) => any;
	updateInternalNotesSubs: (payload: any, workOrder: any) => Promise<any>;
}

const InternalWorkspaceNotesTab = ({
	workOrder,
	messages,
	currentUser,
	searched_subscribers,
	match,
	isClientView,
	getWorkOrder,
	fetchMessages,
	fetchUsers,
	updateInternalNotesSubs,
}: InternalWorkspaceNotesTabProps) => {
	const wsContext = useContext(WebSocketContext);
	const messagesRef = useRef(messages);
	messagesRef.current = messages;
	const [fetching, setFetching] = useState(true);
	const [localMessages, setLocalMessages] = useState<any[]>([]);
	const [, setWsRenderTick] = useState(0);
	const [internalNotesSubscribers, setInternalNotesSubscribers] = useState<any[]>([]);

	const wsStateRef = useRef<WsAdapterState>({
		typingDetails: [],
		newNoteCount: 0,
		refreshCount: 0,
	});
	const prevNotesRef = useRef<any[]>([]);
	const latestPropsRef = useRef({
		workOrder,
		currentUser,
		match,
	});
	latestPropsRef.current = { workOrder, currentUser, match };

	const refreshMessagesRef =
		useRef<(wo?: any, opts?: { clearChannelBeforeFetch?: boolean }) => Promise<any>>();

	const addBuyerSubscribers = useCallback(
		(obj: any, wo: any) => {
			const currentEmails = internalNotesSubscribers.map((s: any) => s.email);
			const newEmails = nullSafeGetOrElse('addBuyerSubscriberEmails', obj, []);
			return updateInternalNotesSubs(
				{ id: nullSafeGet('id', wo), subscriberEmails: [...currentEmails, ...newEmails] },
				wo
			).then((res) => {
				refreshMessagesRef.current && refreshMessagesRef.current(wo);
				return res;
			});
		},
		[updateInternalNotesSubs, internalNotesSubscribers]
	);

	const addSupplierSubscribers = useCallback(
		(obj: any, wo: any) => {
			const currentEmails = internalNotesSubscribers.map((s: any) => s.email);
			const newEmails = nullSafeGetOrElse('addSupplierSubscriberEmails', obj, []);
			return updateInternalNotesSubs(
				{ id: nullSafeGet('id', wo), subscriberEmails: [...currentEmails, ...newEmails] },
				wo
			).then((res) => {
				refreshMessagesRef.current && refreshMessagesRef.current(wo);
				return res;
			});
		},
		[updateInternalNotesSubs, internalNotesSubscribers]
	);

	const removeSupplierSubscriber = useCallback(
		(obj: any, wo: any) => {
			const emailToRemove =
				nullSafeGetOrElse('removeSupplierSubscriberEmails', obj, [])[0] ||
				nullSafeGetOrElse('removeBuyerSubscriberEmails', obj, [])[0];
			const currentEmails = internalNotesSubscribers
				.map((s: any) => s.email)
				.filter((email: string) => email !== emailToRemove);
			return updateInternalNotesSubs(
				{ id: nullSafeGet('id', wo), subscriberEmails: currentEmails },
				wo
			).then((res) => {
				refreshMessagesRef.current && refreshMessagesRef.current(wo);
				return res;
			});
		},
		[updateInternalNotesSubs, internalNotesSubscribers]
	);

	const bumpWsState = useCallback(() => {
		setWsRenderTick((t) => t + 1);
	}, []);

	const refreshMessages = useCallback(
		(wo?: any, opts?: { clearChannelBeforeFetch?: boolean }) => {
			const workOrderId = match.params.id;
			const fetchOpts =
				opts && opts.clearChannelBeforeFetch === true ? { clearChannelBeforeFetch: true } : {};
			return new Promise((resolve, reject) => {
				fetchMessages(workOrderId, fetchOpts)
					.then((res: any) => {
						resolve(res);
						if (res && Array.isArray(res.subscribers)) {
							setInternalNotesSubscribers(res.subscribers);
						}
						const prev = wsStateRef.current;
						wsStateRef.current = {
							...prev,
							refreshCount: (prev.refreshCount || 0) + 1,
							newNoteCount: 0,
							typingDetails: [],
						};
						bumpWsState();
					})
					.catch(reject);
			});
		},
		[bumpWsState, fetchMessages, match.params.id, workOrder]
	);

	refreshMessagesRef.current = refreshMessages;

	const componentAdapterRef = useRef<any>(null);
	if (componentAdapterRef.current === null) {
		componentAdapterRef.current = {
			get props() {
				return latestPropsRef.current;
			},
			get state() {
				return wsStateRef.current;
			},
			setState(updater: any) {
				const prev = wsStateRef.current;
				const patch = typeof updater === 'function' ? updater(prev) : updater;
				wsStateRef.current = { ...prev, ...patch };
				bumpWsState();
			},
			refreshMessages(wo?: any, opts?: { clearChannelBeforeFetch?: boolean }) {
				return refreshMessagesRef.current && refreshMessagesRef.current(wo, opts);
			},
		};
	}

	const wsManagerRef = useRef<WebSocketTypingManager | null>(null);
	if (wsManagerRef.current === null) {
		wsManagerRef.current = new WebSocketTypingManager({
			component: componentAdapterRef.current,
			socketProvider: () => wsContext,
			getEntityIdsFromProps: (comProps) =>
				getOwnThreadWOIdsToListenForWorkOrder(nullSafeGet('workOrder', comProps)),
			getCurrentEntityIdFromProps: (comProps) => nullSafeGet('workOrder.id', comProps),
			entityIdName: 'workOrderId',
			entityType: WEB_SOCKET_ENTITY_TYPES.WORK_ORDER_INTERNAL_NOTES,
			newNoteEntityType: WEB_SOCKET_ENTITY_TYPES.WORK_ORDER_INTERNAL_NOTES,
			chatBoxElementId: 'internalWorkspaceChatBox',
		});
	}

	useMountEffect(() => {
		const wsManager = wsManagerRef.current;
		if (!wsManager) {
			return;
		}
		wsManager.onMount();
		return () => {
			wsManager.onUnmount();
		};
	});

	useEffect(() => {
		wsManagerRef.current && wsManagerRef.current.onUpdate();
	}, [wsContext]);

	useEffect(() => {
		const workOrderId = match.params.id;
		if (!workOrderId) {
			return;
		}

		const getMessages = (wo: any) => {
			const resolvedId = workOrderId;
			const channelId = getInternalNotesChannelId(resolvedId);
			const channelState = messagesRef.current[channelId];
			const alreadyHydrated = !!(channelState && channelState.isLoading === false);

			if (alreadyHydrated) {
				setFetching(false);
				const subs =
					channelState && Array.isArray(channelState.subscribers) ? channelState.subscribers : [];
				setInternalNotesSubscribers(subs);
				setTimeout(scrollChatToBottom, 0);
				return;
			}

			prevNotesRef.current = [];
			setFetching(true);

			refreshMessages(wo, { clearChannelBeforeFetch: true })
				.then(() => {
					setFetching(false);
					setTimeout(scrollChatToBottom, 0);
				})
				.catch(() => {
					setFetching(false);
				});
		};

		if (nullSafeGet('id', workOrder) && String(workOrder.id) === String(workOrderId)) {
			getMessages(workOrder);
		} else {
			getWorkOrder(workOrderId).then((wo) => {
				const sanitised = getSanitisedWorkOrder(wo);
				getMessages(sanitised);
			});
		}
		// Load when route WO id changes; avoid listing refreshMessages/getWorkOrder/workOrder (stale-safe via latest closure on id change)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [match.params.id]);

	const workOrderIdChannel = getInternalNotesChannelId(match.params.id);

	useEffect(() => {
		const newNotes = nullSafeGetOrElse(`${workOrderIdChannel}.messages`, messages, []);
		const prevNotes = prevNotesRef.current;
		if (newNotes !== prevNotes) {
			prevNotesRef.current = newNotes;
			setLocalMessages(newNotes);
			if (newNotes.length > 0) {
				setTimeout(scrollChatToBottom, 0);
			}
		}
	}, [messages, workOrderIdChannel]);

	const actualNotes = localMessages.filter(isNoteValid);
	const currentInternalAttachments = nullSafeGetOrElse(
		'internalAttachments',
		messages[workOrderIdChannel],
		[]
	);

	const subscribers = internalNotesSubscribers.length > 0 ? internalNotesSubscribers : [];
	const filteredUsers = formatAndSortUserContacts(searched_subscribers.records);

	const { firstUnreadMessageIndex, unreadMessageLength } = getUnreadNotesIndexAndCount(
		actualNotes,
		currentUser
	);

	const wsManager = wsManagerRef.current;
	const { typingDetails, newNoteCount, refreshCount } = wsStateRef.current;

	if (fetching) {
		return (
			<div className="flex min-h-0 flex-1 flex-col items-center justify-center">
				<PageLoadingPlaceholder />
			</div>
		);
	}

	return (
		<div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden">
			<WorkOrderNotesSubscribersPanel
				workOrder={workOrder}
				workOrderId={match.params.id}
				subscribers={subscribers}
				filteredUsers={filteredUsers}
				fetchUsers={fetchUsers}
				addBuyerSubscribers={addBuyerSubscribers}
				addSupplierSubscribers={addSupplierSubscribers}
				updateInternalWorkspaceSubscribers={(allEmails, wo) =>
					updateInternalNotesSubs(
						{ id: nullSafeGet('id', wo), subscriberEmails: allEmails },
						wo
					).then((res) => {
						refreshMessagesRef.current && refreshMessagesRef.current(wo);
						return res;
					})
				}
				removeSupplierSubscriber={removeSupplierSubscriber}
				getWorkOrder={(id) =>
					getWorkOrder(id).then((wo) => {
						return wo;
					})
				}
				match={match}
				avatarRowClassName="flex flex-shrink-0 flex-row justify-end px-6 py-2.5"
				isInternalWorkspace={true}
			/>

			<div
				id="internalWorkspaceChatBox"
				className="no-scrollbar flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto py-2 pl-[34px] pr-[34px]"
			>
				{actualNotes.length > 0 ? (
					<div className="flex flex-col gap-3">
						{actualNotes.map((n, idx) => (
							<React.Fragment key={`iw-note-${idx}`}>
								{firstUnreadMessageIndex === idx ? (
									<SeparatorWithText
										className="mt-2 px-2"
										text={`${unreadMessageLength} ${refreshCount > 1 ? 'new' : 'unread'} message${
											unreadMessageLength > 1 ? 's' : ''
										}`}
										textColor={getPrimaryColor(ROLE_TYPES.SUPPLIER)}
										bgColor={getPrimaryColor(ROLE_TYPES.SUPPLIER)}
									/>
								) : null}
								{n.type === NOTE_TYPE_LOG || n.type === NOTE_TYPE_INTERNAL_ATTACHMENT_LOG ? (
									// @ts-ignore — MessageLog is wrapped with withRouter(connect()); generated types omit `log`/`compact`
									<MessageLog log={n} compact internalAttachments={currentInternalAttachments} />
								) : n.type === NOTE_TYPE_SERVICE_CALL_CHECK_IN ||
								  n.type === NOTE_TYPE_SERVICE_CALL_CHECK_OUT ? (
									<ServiceCallLogEntry entry={n} type={n.type} />
								) : (
									<MessageBubble
										n={n}
										currentUser={currentUser}
										isFromInternalNotes
										userType="supplier"
									/>
								)}
							</React.Fragment>
						))}
					</div>
				) : (
					<EmptyState
						graphic={
							<img
								className="mb-2"
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/letter owl.svg"
								alt="Add a note to this work order using the box below."
							/>
						}
						headline={'No notes have been added yet'}
						body={
							<div className="text-center">
								<div className="bodyregular mx-auto mb-4 max-w-md">
									Add a note to this work order using the box below.
								</div>
							</div>
						}
					/>
				)}
			</div>

			<div className="w-full min-w-0 max-w-full shrink-0 overflow-hidden">
				{wsManager ? (
					<InternalWorkspaceWorkOrderNoteComposer
						workOrder={workOrder}
						workOrderId={match.params.id}
						typingDetails={typingDetails}
						newNoteCount={newNoteCount}
						onTyping={wsManager.handleTyping}
						onSendNote={wsManager.onSendNote}
						onRefresh={refreshMessages}
					/>
				) : null}
			</div>
		</div>
	);
};

const mapStateToProps = (state: any, ownProps: any) => ({
	workOrder: getWorkOrderSelector(state, ownProps),
	messages: state.messages,
	currentUser: state.session.currentUser,
	searched_subscribers: state.searched_subscribers,
	match: ownProps.match,
	history: ownProps.history,
	isClientView: state.clientView.isClientView,
});

const mapDispatchToProps = (dispatch: any) => ({
	getWorkOrder: (id: string) => dispatch(workOrdersRestCrudThunksForSupplier.readOne(id)),
	fetchMessages: (workOrderId: string, fetchOpts?: { clearChannelBeforeFetch?: boolean }) =>
		dispatch(fetchInternalWorkspaceForSupplier(workOrderId, fetchOpts)),
	fetchUsers: (search: any, woId: any, filters: any) =>
		dispatch(fetchInternalNotesSubscribersForSupplier(search, woId, filters)()),
	updateInternalNotesSubs: (payload: any, workOrder: any) =>
		dispatch(updateInternalNotesSubscribersForSupplier(payload)(workOrder)),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(InternalWorkspaceNotesTab));
